import EventBus from 'eventing-bus';
import Box from '@material-ui/core/Box';
import * as livekit from 'livekit-client';
import TextField from '@mui/material/TextField';
import { useStopwatch } from 'react-timer-hook';
import React, { useState, useEffect } from 'react';
import { CircularProgress } from "@material-ui/core";
import Typography from '@material-ui/core/Typography';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { Select, MenuItem, FormControl } from '@mui/material';
import { UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';

import '../index.css';
import Functions from './Functions';
import PromptTools from './PromptTools';
import TopSettings from './TopSettings';
import MicSettings from './MicSettings';
import constants from '../../../constants';
import PhoneModal from './AddPhoneModal.js';
import CallLogs from '../../Phone/CallLogs.js';
import useMicDevices from '../../../utils/useMicDevices';
import ViewStateManager from '../../../utils/viewStateManager';
import { getAllPhones, updatePhone } from '../../../store/Phone';
import { getSingleAgent, updateAgent, getConnectionDetails, getVoiceAgentStatus } from '../../../store/Agent';

// =============================
//  Main AgentDetails Component
// =============================

/** AgentDetails - Main component for agent details page **/
const SingleAgent = ({ }) => {

    // ========== Hooks and Redux ==========
    const { id } = useParams();
    const history = useHistory()
    const dispatch = useDispatch()
    const { stats, singleAgent, connectionDetails, isLoading, voiceAgentStatus } = useSelector((state) => state['Agent']);
    const { allPhones } = useSelector((state) => state['Phone']);

    // ========== State Variables ==========
    const [isUpdated, setUpdated] = useState(false);
    const [agentData, setAgentData] = useState({});
    const [isEdited, setIsEdited] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [roomInstance, setRoomInstance] = useState(null);
    const [ishistoryTab, setIsHistoryTab] = useState(false);
    const [isAgentConnected, setIsAgentConnected] = useState(false);
    const [ecsWarmingUp, setEcsWarmingUp] = useState(false);

    const [openWelcome, setOpenWelcome] = useState(false);
    const [searchWelcome, setSearchWelcome] = useState('');
    const [variables, setVariables] = useState([{ name: '', value: '' }]);

    const [speakingState, setSpeakingState] = useState('Connecting'); // Add speaking state
    const { seconds, minutes, isRunning, start, reset } = useStopwatch({ autoStart: false });
    const formattedSeconds = String(seconds).padStart(2, '0');
    const formattedMinutes = String(minutes).padStart(2, '0');

    const [isPhoneModal, setPhoneModal] = useState(false);
    const togglePhoneModal = () => setPhoneModal(prev => !prev);

    const [activeAgentTab, setActiveAgentTab] = useState('prompt'); // 'prompt' or 'call-history'

    // ========== Microphone device selection + live test meter ==========
    const { micState, resolveMicDeviceId, stopMeter, handleMicChange, handleSpeakerChange, registerAudioElement, clearAudioElements, toggleMicPanel } = useMicDevices();

    // ========== Side Effects ==========
    useEffect(() => {
        dispatch({ type: 'CLEAR_CONNECTION_DETAILS' });
        if (id) dispatch(getSingleAgent(id));
        dispatch(getAllPhones());
    }, []);

    useEffect(() => {
        setAgentData(singleAgent);
        if (singleAgent?.dynamicVariables) {
            const variablesArray = Object.entries(singleAgent.dynamicVariables).map(([key, value]) => ({
                name: key,
                value: value
            }));
            setVariables(variablesArray.length > 0 ? variablesArray : [{ name: '', value: '' }]);
        } else if (singleAgent && Object.keys(singleAgent).length > 0) {
            setVariables([{ name: '', value: '' }]);
        }
    }, [singleAgent]);
    useEffect(() => { if (isUpdated) handleUpdateAgent() }, [agentData?.['language'], agentData?.['modelType'], agentData?.['webhookURL'], agentData?.['dynamicVariables'], agentData?.['welcomeMessageType'], agentData?.['llmTemperature']]);
    useEffect(() => { if (connectionDetails && isAgentConnected) initializeAgent(connectionDetails) }, [connectionDetails]);

    // ========== Initialize Agent ==========
    const initializeAgent = async (connectionDetails) => {
        if (!connectionDetails) return console.error('No connection details available');
        try {
            const { serverUrl, participantToken } = connectionDetails;

            const room = new livekit.Room();
            await room.connect(serverUrl, participantToken);
            console.log('Connected to room:', room.name);

            // Publish the mic using the device the user actually selected in the panel. Passing a
            // concrete deviceId avoids the OS 'default' resolving to an unexpected input (e.g. macOS
            // Continuity "iPhone Microphone"), which would publish a silent track while the user
            // talks into a different device.
            const micDeviceId = await resolveMicDeviceId();
            if (micDeviceId) await room.localParticipant.setMicrophoneEnabled(true, { deviceId: { exact: micDeviceId } });
            else await room.localParticipant.setMicrophoneEnabled(true);

            // Start the timer
            start();

            // Track active speakers
            room.on('activeSpeakersChanged', (speakers) => { handleActiveSpeakerChange(speakers) });

            room.on('trackSubscribed', (track) => {
                if (track.kind === 'audio') {
                    const audioElement = track.attach();
                    document.body.appendChild(audioElement);
                    // Route the agent's voice to the user's selected speaker (Chrome/Edge)
                    registerAudioElement(audioElement);
                }
            });

            // Reset the UI when the room ends from the other side (agent hangs up / server disconnects)
            room.on(livekit.RoomEvent.Disconnected, () => { console.log('Room disconnected (remote)'); resetCallUI(); });

            setRoomInstance(room);
            setIsAgentConnected(true);
        } catch (error) {
            console.error('Error connecting to LiveKit:', error);
            setIsAgentConnected(false);
        }
    }

    const handleActiveSpeakerChange = (speakers) => {
        speakers.forEach(speaker => {
            const isAgent = speaker.identity.includes('voice_assistant');
            if (isAgent) setSpeakingState('Listening');
            else setSpeakingState('Speaking');
        });
        if (speakers.length === 0) setSpeakingState('Thinking');
    }

    // ========== Start Agent ==========
    const startAgent = () => {
        // Reset stale status first so the useEffect fires even if value doesn't change
        dispatch({ type: 'SET_VOICE_AGENT_STATUS', payload: null });
        dispatch(getVoiceAgentStatus());
    }

    // React to voiceAgentStatus changes dispatched by the saga
    useEffect(() => {
        if (!voiceAgentStatus) return;
        if (voiceAgentStatus === 'warming_up') { setEcsWarmingUp(true); return; }
        if (voiceAgentStatus === 'ready') {
            // Ready — whether it was already up or just finished warming, auto-connect now.
            // (Previously, finishing a warm-up required a second manual click.)
            setEcsWarmingUp(false);
            setIsAgentConnected(true);
            setRoomInstance(null);
            dispatch(getConnectionDetails({ agentId: agentData['_id'], callType: 'voiceagent-call' }));
        }
    }, [voiceAgentStatus]);

    // Poll ECS status every 15s while container is warming up
    useEffect(() => {
        if (!ecsWarmingUp) return;
        const interval = setInterval(() => dispatch(getVoiceAgentStatus()), 15000);
        return () => clearInterval(interval);
    }, [ecsWarmingUp]);

    // ========== Reset call UI/state (shared by user End Call and remote disconnect) ==========
    const resetCallUI = () => {
        setRoomInstance(null);
        reset(new Date(), false);
        setIsAgentConnected(false);
        setSpeakingState('Connecting');
        stopMeter();
        clearAudioElements();
    }

    // ========== Disconnect Room (user clicked End Call) ==========
    const disconnectAgent = () => {
        if (roomInstance) {
            roomInstance.disconnect();
            resetCallUI();
            console.log('Previous room disconnected');
        }
    }

    // ========== Microphone settings split-button (wraps a trigger with caret + inline panel) ==========
    const renderMicSettings = (children) => (<MicSettings micState={micState} roomInstance={roomInstance} toggleMicPanel={toggleMicPanel} handleMicChange={handleMicChange} handleSpeakerChange={handleSpeakerChange}>{children}</MicSettings>);

    // ========== Update Agent ==========
    const handleUpdateAgent = (file = selectedFile) => {
        setUpdated(false);
        const formData = new FormData();
        const agentKeys = ['name', 'script', 'firstMessage', 'welcomeMessageType', 'modelType', 'language', 'webhookURL', 'dynamicVariables', 'llmTemperature'];
        for (const key of agentKeys) if (agentData[key] !== undefined)
            if (agentData[key] && typeof agentData[key] === 'object') formData.append(key, JSON.stringify(agentData[key]));
            else formData.append(key, agentData[key]);

        if (agentData['webhookURL'] === '') formData.append('webhookURL', '');

        formData.append('voiceId', agentData['voiceId']?.['_id'] || '');
        formData.append('modelId', agentData['modelId']?.['_id'] || '');

        if (file) formData.append('file', file);

        dispatch(updateAgent(id, formData, () => setSelectedFile(null), () => console.log('Update failed')));
    }

    // ========== Apply AI-approved prompt as a new version ==========
    const handleApplyAiPrompt = (proposedScript, feedback) => {
        const formData = new FormData();
        formData.append('script', proposedScript);
        formData.append('changeSource', 'ai');
        formData.append('aiFeedback', feedback);
        dispatch(updateAgent(id, formData));
    }

    const filteredWelcomeMessage = constants['welcomeMessage'].filter((status) => status['label'].toLowerCase().includes(searchWelcome.toLowerCase()));
    const handleWelcomeMessage = (value) => {
        setSearchWelcome('');
        setOpenWelcome(false);
        const selectedOption = constants['welcomeMessage'].find((option) => option['label'] === value);
        if (selectedOption) {
            setAgentData({ ...agentData, welcomeMessageType: selectedOption.value });
            setUpdated(true);
        }
    };

    // ======== Color Classes based on Cost & Latency ========
    const costTotal = parseFloat(stats?.['cost']?.['total'] || 0);
    let costClass = 'green-bg';
    if (costTotal > 0.075) costClass = 'orange-bg';
    else if (costTotal > 0.1) costClass = 'red-bg';

    const latencyTotal = parseFloat(stats?.['latency']?.['total'] || 0);
    let latencyClass = 'green-bg';
    if (latencyTotal > 1000) latencyClass = 'red-bg';
    else if (latencyTotal > 750) latencyClass = 'orange-bg';

    const [showVariablesBox, setShowVariablesBox] = useState(false);
    const [expandedAgent, setExpandedAgent] = useState(false);

    const toggleContentVeriablesBox = () => setShowVariablesBox(!showVariablesBox);

    // Common method for variables actions
    const handleVariablesAction = (action, index = null, field = null, value = null) => {
        switch (action) {
            case 'add':
                setVariables(prev => [...prev, { name: '', value: '' }]);
                break;

            case 'remove':
                if (index !== null) setVariables(prev => prev.filter((_, i) => i !== index));
                break;

            case 'change':
                if (index !== null && field && value !== null) {
                    setVariables(prev => {
                        const updated = [...prev];
                        updated[index][field] = value;
                        return updated;
                    });
                }
                break;

            case 'save':
                const dynamicVariablesObject = {};
                const duplicateNames = [];
                const variableNames = new Set();

                variables.forEach((item) => {
                    const trimmedName = item.name.trim();
                    const trimmedValue = item.value.trim();

                    if (trimmedName && trimmedValue) {
                        if (variableNames.has(trimmedName)) duplicateNames.push(trimmedName);
                        else variableNames.add(trimmedName), dynamicVariablesObject[trimmedName] = trimmedValue;
                    }
                });

                if (duplicateNames.length > 0) {
                    EventBus.publish('error', `Duplicate variable names found: ${duplicateNames.join(', ')}`);
                    return;
                }

                setAgentData(prev => ({ ...prev, dynamicVariables: dynamicVariablesObject }));
                setUpdated(true);
                setShowVariablesBox(false);
                break;

            case 'cancel':
                setShowVariablesBox(false);
                break;

            default:
                console.warn('Unknown variables action:', action);
        }
    };

     useEffect(() => {
        document.body.classList.add("hide-layout");
        console.log("Added workflow-open class to body");

        return () => {
            document.body.classList.remove("hide-layout");
            console.log("Removed workflow-open class from body");
        };
    }, []);

    const handleBackClick = () => {
        history.push('/agents');
        document.body.classList.remove("hide-layout");
        localStorage.removeItem("hide-layout"); // clear state
    }

    return (
        <div className='single-agent-page single-agent-page-new'>
            {/* ========== Header Section ========== */}
            <div className='page-header-new style-two'>
                <h2>
                    <button onClick={handleBackClick} className='back-btn' >
                        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect x="0.4375" y="0.4375" width="31.125" height="31.125" rx="4.1925" fill="white" stroke="#E5EAF0" stroke-width="0.875" />
                            <path d="M19 20.3177C19 20.091 18.9165 19.8642 18.7374 19.6852L15.1571 16.1049L18.7374 12.5246C19.0835 12.1785 19.0835 11.6057 18.7374 11.2596C18.3913 10.9135 17.8185 10.9135 17.4724 11.2596L13.2596 15.4724C12.9135 15.8185 12.9135 16.3913 13.2596 16.7374L17.4724 20.9503C17.8185 21.2964 18.3913 21.2964 18.7374 20.9503C18.9165 20.7713 19 20.5445 19 20.3177Z" fill="var(--primary-color)" />
                        </svg>
                    </button>
                    <div className='right-heading-area'>
                        {isEditing ?
                            <>
                                <input
                                    autoFocus
                                    type='text'
                                    name='name'
                                    className='editable-input'
                                    value={agentData?.['name'] || ''}
                                    onBlur={() => { setIsEditing(false); handleUpdateAgent(); }}
                                    onChange={(e) => setAgentData({ ...agentData, [e['target']['name']]: e['target']['value'] })}
                                />
                                <span className='edit-icon' onClick={() => setIsEditing(false)}>
                                    <svg width="18" height="17" viewBox="0 0 18 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M4.42391 13.8267C3.99183 13.8267 3.58808 13.6779 3.29766 13.4017C2.92933 13.0546 2.75225 12.5304 2.816 11.9638L3.07808 9.66876C3.12766 9.23668 3.38975 8.66293 3.69433 8.35126L9.50975 2.19585C10.9618 0.658764 12.4777 0.616265 14.0147 2.06835C15.5518 3.52043 15.5943 5.03626 14.1422 6.57335L8.32683 12.7288C8.02933 13.0475 7.47683 13.345 7.04475 13.4158L4.76391 13.8054C4.6435 13.8125 4.53725 13.8267 4.42391 13.8267ZM11.7835 2.06126C11.2381 2.06126 10.7635 2.40126 10.2818 2.91126L4.46641 9.07376C4.32475 9.22252 4.16183 9.57668 4.1335 9.7821L3.87141 12.0771C3.84308 12.3108 3.89975 12.5021 4.02725 12.6225C4.15475 12.7429 4.346 12.7854 4.57975 12.75L6.86058 12.3604C7.066 12.325 7.406 12.1408 7.54766 11.9921L13.3631 5.83668C14.2414 4.90168 14.5602 4.03751 13.2781 2.83335C12.7114 2.28793 12.2227 2.06126 11.7835 2.06126Z" fill="#4B5563" />
                                        <path d="M12.7825 7.75643C12.7684 7.75643 12.7471 7.75643 12.7329 7.75643C10.5229 7.53684 8.74502 5.8581 8.40502 3.66226C8.36252 3.37185 8.56086 3.10268 8.85127 3.05309C9.14169 3.01059 9.41086 3.20893 9.46044 3.49935C9.72961 5.21351 11.1179 6.53101 12.8463 6.70101C13.1367 6.72935 13.3492 6.99143 13.3209 7.28184C13.2854 7.55101 13.0517 7.75643 12.7825 7.75643Z" fill="#4B5563" />
                                        <path d="M15.375 16.1147H2.625C2.33458 16.1147 2.09375 15.8739 2.09375 15.5835C2.09375 15.2931 2.33458 15.0522 2.625 15.0522H15.375C15.6654 15.0522 15.9062 15.2931 15.9062 15.5835C15.9062 15.8739 15.6654 16.1147 15.375 16.1147Z" fill="#4B5563" />
                                    </svg>
                                </span>
                            </>
                            :
                            <>
                                <span className='agent-name' onClick={() => setIsEditing(true)}> {isLoading ? 'Loading...' : agentData['name']} </span>
                                {!isEditing && !isLoading && (<span className='edit-icon' onClick={() => setIsEditing(true)}>
                                    <svg width="18" height="17" viewBox="0 0 18 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M4.42391 13.8267C3.99183 13.8267 3.58808 13.6779 3.29766 13.4017C2.92933 13.0546 2.75225 12.5304 2.816 11.9638L3.07808 9.66876C3.12766 9.23668 3.38975 8.66293 3.69433 8.35126L9.50975 2.19585C10.9618 0.658764 12.4777 0.616265 14.0147 2.06835C15.5518 3.52043 15.5943 5.03626 14.1422 6.57335L8.32683 12.7288C8.02933 13.0475 7.47683 13.345 7.04475 13.4158L4.76391 13.8054C4.6435 13.8125 4.53725 13.8267 4.42391 13.8267ZM11.7835 2.06126C11.2381 2.06126 10.7635 2.40126 10.2818 2.91126L4.46641 9.07376C4.32475 9.22252 4.16183 9.57668 4.1335 9.7821L3.87141 12.0771C3.84308 12.3108 3.89975 12.5021 4.02725 12.6225C4.15475 12.7429 4.346 12.7854 4.57975 12.75L6.86058 12.3604C7.066 12.325 7.406 12.1408 7.54766 11.9921L13.3631 5.83668C14.2414 4.90168 14.5602 4.03751 13.2781 2.83335C12.7114 2.28793 12.2227 2.06126 11.7835 2.06126Z" fill="#4B5563" />
                                        <path d="M12.7825 7.75643C12.7684 7.75643 12.7471 7.75643 12.7329 7.75643C10.5229 7.53684 8.74502 5.8581 8.40502 3.66226C8.36252 3.37185 8.56086 3.10268 8.85127 3.05309C9.14169 3.01059 9.41086 3.20893 9.46044 3.49935C9.72961 5.21351 11.1179 6.53101 12.8463 6.70101C13.1367 6.72935 13.3492 6.99143 13.3209 7.28184C13.2854 7.55101 13.0517 7.75643 12.7825 7.75643Z" fill="#4B5563" />
                                        <path d="M15.375 16.1147H2.625C2.33458 16.1147 2.09375 15.8739 2.09375 15.5835C2.09375 15.2931 2.33458 15.0522 2.625 15.0522H15.375C15.6654 15.0522 15.9062 15.2931 15.9062 15.5835C15.9062 15.8739 15.6654 16.1147 15.375 16.1147Z" fill="#4B5563" />
                                    </svg>
                                </span>)}
                            </>
                        }
                        <div className='list-area'>
                            <p>Single prompt</p>
                            <ul>
                                {/* orange-bg | red-bg | green-bg */}
                                <li className=''>
                                    <h4>Agent ID: {agentData?._id ? `${agentData._id.slice(0, 4)}.....${agentData._id.slice(-4)}` : 'Loading...'}
                                        <CopyToClipboard text={agentData?._id} onCopy={() => EventBus.publish('success', 'Address Copied!')}>
                                            <span className='copy-btn'>
                                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M5.55 11.375H3.45C1.495 11.375 0.625 10.505 0.625 8.55V6.45C0.625 4.495 1.495 3.625 3.45 3.625H5.55C7.505 3.625 8.375 4.495 8.375 6.45V8.55C8.375 10.505 7.505 11.375 5.55 11.375ZM3.45 4.375C1.9 4.375 1.375 4.9 1.375 6.45V8.55C1.375 10.1 1.9 10.625 3.45 10.625H5.55C7.1 10.625 7.625 10.1 7.625 8.55V6.45C7.625 4.9 7.1 4.375 5.55 4.375H3.45Z" fill="#4B5563" />
                                                    <path d="M8.55 8.375H8C7.795 8.375 7.625 8.205 7.625 8V6.45C7.625 4.9 7.1 4.375 5.55 4.375H4C3.795 4.375 3.625 4.205 3.625 4V3.45C3.625 1.495 4.495 0.625 6.45 0.625H8.55C10.505 0.625 11.375 1.495 11.375 3.45V5.55C11.375 7.505 10.505 8.375 8.55 8.375ZM8.375 7.625H8.55C10.1 7.625 10.625 7.1 10.625 5.55V3.45C10.625 1.9 10.1 1.375 8.55 1.375H6.45C4.9 1.375 4.375 1.9 4.375 3.45V3.625H5.55C7.505 3.625 8.375 4.495 8.375 6.45V7.625Z" fill="#4B5563" />
                                                </svg>
                                            </span>
                                        </CopyToClipboard>
                                    </h4>
                                </li>
                                <li className={costClass}>
                                    <h4>${parseFloat(costTotal).toFixed(2) || 0}/min
                                        <i className='icon'>
                                            <span className=''>
                                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M6 0.625C3.035 0.625 0.625 3.035 0.625 6C0.625 8.965 3.035 11.375 6 11.375C8.965 11.375 11.375 8.965 11.375 6C11.375 3.035 8.965 0.625 6 0.625ZM6 10.625C3.45 10.625 1.375 8.55 1.375 6C1.375 3.45 3.45 1.375 6 1.375C8.55 1.375 10.625 3.45 10.625 6C10.625 8.55 8.55 10.625 6 10.625Z" fill="#4B5563" />
                                                    <path d="M6 5.125C5.795 5.125 5.625 5.295 5.625 5.5V8C5.625 8.205 5.795 8.375 6 8.375C6.205 8.375 6.375 8.205 6.375 8V5.5C6.375 5.295 6.205 5.125 6 5.125Z" fill="#4B5563" />
                                                    <path d="M6 3.50006C5.935 3.50006 5.87 3.51506 5.81 3.54006C5.75 3.56506 5.695 3.60006 5.645 3.64506C5.6 3.69506 5.565 3.74506 5.54 3.81006C5.515 3.87006 5.5 3.93506 5.5 4.00006C5.5 4.06506 5.515 4.13006 5.54 4.19006C5.565 4.25006 5.6 4.30506 5.645 4.35506C5.695 4.40006 5.75 4.43506 5.81 4.46006C5.93 4.51006 6.07 4.51006 6.19 4.46006C6.25 4.43506 6.305 4.40006 6.355 4.35506C6.4 4.30506 6.435 4.25006 6.46 4.19006C6.485 4.13006 6.5 4.06506 6.5 4.00006C6.5 3.93506 6.485 3.87006 6.46 3.81006C6.435 3.74506 6.4 3.69506 6.355 3.64506C6.305 3.60006 6.25 3.56506 6.19 3.54006C6.13 3.51506 6.065 3.50006 6 3.50006Z" fill="#4B5563" />
                                                </svg>
                                            </span>
                                            <div className='overlay-box'>
                                                <div className='inner-box'>
                                                    <div className='tim-box'>
                                                        <h4>STT</h4>
                                                        <p>${parseFloat(stats?.['cost']?.['stt'] || 0).toFixed(2)}/min</p>
                                                    </div>
                                                    <div className='tim-box'>
                                                        <h4>LLM</h4>
                                                        <p>${parseFloat(stats?.['cost']?.['llm'] || 0).toFixed(2)}/min</p>
                                                    </div>
                                                    <div className='tim-box'>
                                                        <h4>TTS</h4>
                                                        <p>${parseFloat(stats?.['cost']?.['tts'] || 0).toFixed(2)}/min</p>
                                                    </div>
                                                    <div className='tim-box'>
                                                        <h4>Total</h4>
                                                        <p>${parseFloat(stats?.['cost']?.['total'] || 0).toFixed(2)}/min</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </i>
                                    </h4>
                                </li>
                                <li className={latencyClass}>
                                    <h4>Estimated Latency: {latencyTotal || 0}ms
                                        <i className='icon'>
                                            <span className=''>
                                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M6 0.625C3.035 0.625 0.625 3.035 0.625 6C0.625 8.965 3.035 11.375 6 11.375C8.965 11.375 11.375 8.965 11.375 6C11.375 3.035 8.965 0.625 6 0.625ZM6 10.625C3.45 10.625 1.375 8.55 1.375 6C1.375 3.45 3.45 1.375 6 1.375C8.55 1.375 10.625 3.45 10.625 6C10.625 8.55 8.55 10.625 6 10.625Z" fill="#4B5563" />
                                                    <path d="M6 5.125C5.795 5.125 5.625 5.295 5.625 5.5V8C5.625 8.205 5.795 8.375 6 8.375C6.205 8.375 6.375 8.205 6.375 8V5.5C6.375 5.295 6.205 5.125 6 5.125Z" fill="#4B5563" />
                                                    <path d="M6 3.50006C5.935 3.50006 5.87 3.51506 5.81 3.54006C5.75 3.56506 5.695 3.60006 5.645 3.64506C5.6 3.69506 5.565 3.74506 5.54 3.81006C5.515 3.87006 5.5 3.93506 5.5 4.00006C5.5 4.06506 5.515 4.13006 5.54 4.19006C5.565 4.25006 5.6 4.30506 5.645 4.35506C5.695 4.40006 5.75 4.43506 5.81 4.46006C5.93 4.51006 6.07 4.51006 6.19 4.46006C6.25 4.43506 6.305 4.40006 6.355 4.35506C6.4 4.30506 6.435 4.25006 6.46 4.19006C6.485 4.13006 6.5 4.06506 6.5 4.00006C6.5 3.93506 6.485 3.87006 6.46 3.81006C6.435 3.74506 6.4 3.69506 6.355 3.64506C6.305 3.60006 6.25 3.56506 6.19 3.54006C6.13 3.51506 6.065 3.50006 6 3.50006Z" fill="#4B5563" />
                                                </svg>
                                            </span>
                                            <div className='overlay-box'>
                                                <div className='inner-box'>
                                                    <div className='tim-box'>
                                                        <h4>STT</h4>
                                                        <p>{stats?.['latency']?.['stt'] || 0}ms</p>
                                                    </div>
                                                    <div className='tim-box'>
                                                        <h4>LLM</h4>
                                                        <p>{stats?.['latency']?.['llm'] || 0}ms</p>
                                                    </div>
                                                    <div className='tim-box'>
                                                        <h4>TTS</h4>
                                                        <p>{stats?.['latency']?.['tts'] || 0}ms</p>
                                                    </div>
                                                    <div className='tim-box'>
                                                        <h4>Total</h4>
                                                        <p>{stats?.['latency']?.['total'] || 0}ms</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </i>
                                    </h4>
                                </li>
                            </ul>
                        </div>
                    </div>
                </h2>
                {/* Agent Tabs - Prompt and Call History - Centered */}
                <div className="single-agent-page-tabs">
                        <button
                            onClick={() => setActiveAgentTab('prompt')}
                            className={activeAgentTab === 'prompt' ? 'active' : ''}
                        >
                            <i className="icon"><svg width="23" height="20" viewBox="0 0 23 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20.0784 14.7194C19.9785 15.789 19.641 16.7644 19.0031 17.6185C17.8657 19.1404 16.3431 19.9588 14.4394 19.9824C12.2146 20.0095 9.98909 20.0009 7.7636 19.9859C4.84741 19.9667 2.43497 17.754 2.13101 14.8471C2.12673 14.8043 2.1146 14.7622 2.10746 14.7244C0.792433 14.4632 0.069629 13.7683 0.00469798 12.7008C-0.033119 12.0851 0.157393 11.5442 0.57552 11.0875C0.986512 10.6387 1.51381 10.4325 2.11317 10.3854C2.20522 9.3501 2.51917 8.41252 3.10997 7.58412C4.26374 5.96726 5.84635 5.14029 7.83138 5.10318C8.66764 5.08748 9.5046 5.10033 10.3416 5.10033C10.4386 5.10033 10.5357 5.10033 10.6719 5.10033C10.6719 4.81777 10.6719 4.55377 10.6719 4.28976C10.6719 3.85379 10.6648 3.41712 10.6755 2.98115C10.6798 2.81918 10.6355 2.73641 10.4793 2.66006C9.86636 2.36181 9.56882 1.70108 9.73079 1.05177C9.88634 0.429575 10.4536 -0.00424976 11.1065 3.14058e-05C11.7679 0.00431257 12.3152 0.447413 12.4679 1.10172C12.612 1.71963 12.3045 2.35253 11.7165 2.66648C11.6416 2.70644 11.5824 2.78279 11.531 2.85343C11.5096 2.88268 11.5267 2.93977 11.5267 2.98472C11.5267 3.67755 11.5267 4.36968 11.5267 5.09961C11.6509 5.09961 11.7544 5.0989 11.8571 5.09961C12.8803 5.11103 13.9064 5.07749 14.926 5.14243C17.4697 5.30582 19.7166 7.46853 20.0291 10.0015C20.0441 10.1243 20.0641 10.247 20.0812 10.3633C21.5818 10.7729 22.2411 11.4878 22.1826 12.6323C22.1269 13.7661 21.382 14.5125 20.0784 14.7194ZM11.0929 19.1347C12.1696 19.1347 13.2463 19.1404 14.3231 19.1333C17.1001 19.1154 19.22 16.9948 19.2328 14.2242C19.2378 13.1033 19.24 11.9816 19.2293 10.8606C19.2264 10.5331 19.205 10.1999 19.1387 9.88025C18.6563 7.54131 16.707 5.96441 14.2888 5.95513C12.251 5.94729 10.2131 5.95228 8.1753 5.95513C7.88204 5.95585 7.58807 5.96798 7.29624 5.99081C5.13568 6.16277 3.1592 8.05362 3.00936 10.2163C2.90947 11.6512 2.97083 13.0968 2.96512 14.5382C2.96441 14.7058 2.98796 14.8757 3.01864 15.0419C3.46388 17.49 5.42109 19.1261 7.91629 19.134C8.97445 19.1383 10.0333 19.1347 11.0929 19.1347ZM20.1091 11.2909C20.1091 12.1357 20.1091 12.9812 20.1091 13.8275C20.8604 13.7775 21.3884 13.1696 21.3278 12.4447C21.2728 11.7775 20.7056 11.2381 20.1091 11.2909ZM2.07036 13.8303C2.07036 12.967 2.07036 12.1214 2.07036 11.2752C1.39679 11.3016 0.872348 11.8446 0.855223 12.5103C0.838098 13.2046 1.34399 13.7618 2.07036 13.8303ZM11.0908 0.856978C10.7897 0.859119 10.5528 1.09387 10.5492 1.39426C10.5456 1.70536 10.7904 1.94939 11.1029 1.94582C11.4054 1.94297 11.6395 1.70964 11.6438 1.40854C11.6473 1.09744 11.4047 0.854838 11.0908 0.856978Z" fill="currentColor"></path><path d="M11.0858 16.6408C9.91135 16.6408 8.73688 16.6429 7.56241 16.6401C5.78644 16.6365 4.59913 15.4499 4.59699 13.6782C4.59628 12.904 4.59414 12.1298 4.59771 11.3557C4.60484 9.65747 5.81498 8.45161 7.51389 8.4509C9.8985 8.44947 12.2831 8.44947 14.6684 8.4509C16.3773 8.45161 17.5753 9.63678 17.5868 11.3464C17.5925 12.2362 17.6046 13.1266 17.5739 14.0157C17.5268 15.3999 16.3638 16.5837 14.9845 16.6222C13.6866 16.6586 12.3866 16.6301 11.0872 16.6301C11.0858 16.6336 11.0858 16.6372 11.0858 16.6408ZM11.0801 15.786C12.2902 15.786 13.4997 15.7917 14.7091 15.7845C15.9071 15.7767 16.7184 14.969 16.7298 13.776C16.7377 12.9575 16.737 12.1391 16.7298 11.3207C16.7191 10.117 15.8936 9.30642 14.6856 9.30571C12.2924 9.30428 9.89993 9.30428 7.50676 9.30571C6.2802 9.30642 5.45537 10.1363 5.4518 11.3707C5.44966 12.1356 5.45109 12.9005 5.4518 13.6654C5.45251 14.9754 6.25309 15.7824 7.55813 15.7845C8.73189 15.7874 9.90564 15.786 11.0801 15.786Z" fill="currentColor"></path><path d="M8.341 14.3347C7.35419 14.3305 6.5636 13.5363 6.5636 12.5488C6.5636 11.552 7.3706 10.7457 8.36312 10.7521C9.34208 10.7585 10.1441 11.5648 10.1455 12.5438C10.1462 13.5384 9.33994 14.3383 8.341 14.3347ZM8.31745 13.4785C8.8476 13.4928 9.28214 13.0832 9.28999 12.5609C9.29784 12.0507 8.8847 11.6212 8.37382 11.6076C7.86507 11.5941 7.43054 12.0093 7.41912 12.5188C7.4077 13.0411 7.80371 13.4642 8.31745 13.4785Z" fill="currentColor"></path><path d="M13.846 14.3347C12.8464 14.3383 12.0465 13.5384 12.0508 12.5402C12.0551 11.5555 12.8521 10.7557 13.8325 10.7521C14.8193 10.7485 15.6406 11.5691 15.6348 12.553C15.6299 13.5356 14.8307 14.3311 13.846 14.3347ZM13.8418 13.4785C14.3562 13.477 14.7765 13.0611 14.7786 12.5502C14.7808 12.035 14.3384 11.5976 13.8225 11.6076C13.3109 11.6169 12.902 12.0407 12.9063 12.5559C12.9113 13.076 13.3209 13.4799 13.8418 13.4785Z" fill="currentColor"></path></svg></i>
                            Agent
                        </button>
                        <button
                            onClick={() => setActiveAgentTab('call-history')}
                            className={activeAgentTab === 'call-history' ? 'active' : ''}
                        >
                            <i className="icon"><svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13.0875 17.0625C12.24 17.0625 11.3475 16.86 10.425 16.47C9.525 16.0875 8.6175 15.5625 7.7325 14.925C6.855 14.28 6.0075 13.56 5.205 12.7725C4.41 11.97 3.69 11.1225 3.0525 10.2525C2.4075 9.3525 1.89 8.4525 1.5225 7.5825C1.1325 6.6525 0.9375 5.7525 0.9375 4.905C0.9375 4.32 1.0425 3.765 1.245 3.2475C1.455 2.715 1.7925 2.22 2.25 1.7925C2.8275 1.2225 3.4875 0.9375 4.1925 0.9375C4.485 0.9375 4.785 1.005 5.04 1.125C5.3325 1.26 5.58 1.4625 5.76 1.7325L7.5 4.185C7.6575 4.4025 7.7775 4.6125 7.86 4.8225C7.9575 5.0475 8.01 5.2725 8.01 5.49C8.01 5.775 7.9275 6.0525 7.77 6.315C7.6575 6.5175 7.485 6.735 7.2675 6.9525L6.7575 7.485C6.765 7.5075 6.7725 7.5225 6.78 7.5375C6.87 7.695 7.05 7.965 7.395 8.37C7.7625 8.79 8.1075 9.1725 8.4525 9.525C8.895 9.96 9.2625 10.305 9.6075 10.59C10.035 10.95 10.3125 11.13 10.4775 11.2125L10.4625 11.25L11.01 10.71C11.2425 10.4775 11.4675 10.305 11.685 10.1925C12.0975 9.9375 12.6225 9.8925 13.1475 10.11C13.3425 10.1925 13.5525 10.305 13.7775 10.4625L16.2675 12.2325C16.545 12.42 16.7475 12.66 16.8675 12.945C16.98 13.23 17.0325 13.4925 17.0325 13.755C17.0325 14.115 16.95 14.475 16.7925 14.8125C16.635 15.15 16.44 15.4425 16.1925 15.7125C15.765 16.185 15.3 16.5225 14.76 16.74C14.2425 16.95 13.68 17.0625 13.0875 17.0625ZM4.1925 2.0625C3.78 2.0625 3.3975 2.2425 3.03 2.6025C2.685 2.925 2.445 3.2775 2.295 3.66C2.1375 4.05 2.0625 4.4625 2.0625 4.905C2.0625 5.6025 2.2275 6.36 2.5575 7.14C2.895 7.935 3.3675 8.76 3.9675 9.585C4.5675 10.41 5.25 11.2125 6 11.97C6.75 12.7125 7.56 13.4025 8.3925 14.01C9.2025 14.6025 10.035 15.0825 10.86 15.4275C12.1425 15.975 13.3425 16.1025 14.3325 15.69C14.715 15.5325 15.0525 15.2925 15.36 14.9475C15.5325 14.76 15.6675 14.5575 15.78 14.3175C15.87 14.13 15.915 13.935 15.915 13.74C15.915 13.62 15.8925 13.5 15.8325 13.365C15.81 13.32 15.765 13.2375 15.6225 13.14L13.1325 11.37C12.9825 11.265 12.8475 11.19 12.72 11.1375C12.555 11.07 12.4875 11.0025 12.2325 11.16C12.0825 11.235 11.9475 11.3475 11.7975 11.4975L11.2275 12.06C10.935 12.345 10.485 12.4125 10.14 12.285L9.9375 12.195C9.63 12.03 9.27 11.775 8.8725 11.4375C8.5125 11.13 8.1225 10.77 7.65 10.305C7.2825 9.93 6.915 9.5325 6.5325 9.09C6.18 8.6775 5.925 8.325 5.7675 8.0325L5.6775 7.8075C5.6325 7.635 5.6175 7.5375 5.6175 7.4325C5.6175 7.1625 5.715 6.9225 5.9025 6.735L6.465 6.15C6.615 6 6.7275 5.8575 6.8025 5.73C6.8625 5.6325 6.885 5.55 6.885 5.475C6.885 5.415 6.8625 5.325 6.825 5.235C6.7725 5.115 6.69 4.98 6.585 4.8375L4.845 2.3775C4.77 2.2725 4.68 2.1975 4.5675 2.145C4.4475 2.0925 4.32 2.0625 4.1925 2.0625ZM10.4625 11.2575L10.3425 11.7675L10.545 11.2425C10.5075 11.235 10.4775 11.2425 10.4625 11.2575Z" fill="currentColor"></path></svg></i>
                            Call History
                        </button>
                </div>
                <div className='right-btn-area'>
                    {/* <button className="btn-attched">
                        <svg width="16" height="4" viewBox="0 0 16 4" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="2" cy="2" r="2" transform="rotate(-90 2 2)" fill="#4B5563" />
                            <circle cx="8" cy="2" r="2" transform="rotate(-90 8 2)" fill="#4B5563" />
                            <circle cx="14" cy="2" r="2" transform="rotate(-90 14 2)" fill="#4B5563" />
                        </svg>
                    </button> */}
                    <button className="btn-style-new" onClick={togglePhoneModal}>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M10.1792 13.2709C9.52008 13.2709 8.82591 13.1134 8.10841 12.8101C7.40841 12.5126 6.70258 12.1042 6.01425 11.6084C5.33175 11.1067 4.67258 10.5467 4.04841 9.93425C3.43008 9.31008 2.87008 8.65092 2.37425 7.97425C1.87258 7.27425 1.47008 6.57425 1.18425 5.89758C0.880915 5.17425 0.729248 4.47425 0.729248 3.81508C0.729248 3.36008 0.810915 2.92841 0.968415 2.52591C1.13175 2.11175 1.39425 1.72675 1.75008 1.39425C2.19925 0.950915 2.71258 0.729248 3.26091 0.729248C3.48841 0.729248 3.72175 0.781748 3.92008 0.875081C4.14758 0.980081 4.34008 1.13758 4.48008 1.34758L5.83341 3.25508C5.95591 3.42425 6.04925 3.58758 6.11341 3.75091C6.18925 3.92591 6.23008 4.10091 6.23008 4.27008C6.23008 4.49175 6.16591 4.70758 6.04341 4.91175C5.95591 5.06925 5.82175 5.23842 5.65258 5.40758L5.25591 5.82175C5.26175 5.83925 5.26758 5.85091 5.27341 5.86258C5.34341 5.98508 5.48341 6.19508 5.75175 6.51008C6.03758 6.83675 6.30591 7.13425 6.57425 7.40841C6.91841 7.74675 7.20425 8.01508 7.47258 8.23675C7.80508 8.51675 8.02091 8.65675 8.14925 8.72092L8.13758 8.75008L8.56341 8.33008C8.74425 8.14925 8.91925 8.01508 9.08841 7.92758C9.40925 7.72925 9.81758 7.69425 10.2259 7.86341C10.3776 7.92758 10.5409 8.01508 10.7159 8.13758L12.6526 9.51425C12.8684 9.66008 13.0259 9.84675 13.1192 10.0684C13.2067 10.2901 13.2476 10.4942 13.2476 10.6984C13.2476 10.9784 13.1834 11.2584 13.0609 11.5209C12.9384 11.7834 12.7867 12.0109 12.5942 12.2209C12.2617 12.5884 11.9001 12.8509 11.4801 13.0201C11.0776 13.1834 10.6401 13.2709 10.1792 13.2709ZM3.26091 1.60425C2.94008 1.60425 2.64258 1.74425 2.35675 2.02425C2.08841 2.27508 1.90175 2.54925 1.78508 2.84675C1.66258 3.15008 1.60425 3.47091 1.60425 3.81508C1.60425 4.35758 1.73258 4.94675 1.98925 5.55342C2.25175 6.17175 2.61925 6.81342 3.08591 7.45508C3.55258 8.09675 4.08341 8.72092 4.66675 9.31008C5.25008 9.88758 5.88008 10.4242 6.52758 10.8967C7.15758 11.3576 7.80508 11.7309 8.44675 11.9992C9.44425 12.4251 10.3776 12.5242 11.1476 12.2034C11.4451 12.0809 11.7076 11.8942 11.9467 11.6259C12.0809 11.4801 12.1859 11.3226 12.2734 11.1359C12.3434 10.9901 12.3784 10.8384 12.3784 10.6867C12.3784 10.5934 12.3609 10.5001 12.3142 10.3951C12.2967 10.3601 12.2617 10.2959 12.1509 10.2201L10.2142 8.84342C10.0976 8.76175 9.99258 8.70342 9.89341 8.66258C9.76508 8.61008 9.71258 8.55758 9.51425 8.68008C9.39758 8.73842 9.29258 8.82592 9.17591 8.94258L8.73258 9.38008C8.50508 9.60175 8.15508 9.65425 7.88675 9.55508L7.72925 9.48508C7.49008 9.35675 7.21008 9.15841 6.90091 8.89592C6.62091 8.65675 6.31758 8.37675 5.95008 8.01508C5.66425 7.72341 5.37841 7.41425 5.08091 7.07008C4.80675 6.74925 4.60841 6.47508 4.48591 6.24758L4.41591 6.07258C4.38091 5.93842 4.36925 5.86258 4.36925 5.78091C4.36925 5.57091 4.44508 5.38425 4.59091 5.23841L5.02841 4.78341C5.14508 4.66675 5.23258 4.55591 5.29091 4.45675C5.33758 4.38091 5.35508 4.31675 5.35508 4.25841C5.35508 4.21175 5.33758 4.14175 5.30841 4.07175C5.26758 3.97842 5.20341 3.87341 5.12175 3.76258L3.76841 1.84925C3.71008 1.76758 3.64008 1.70925 3.55258 1.66841C3.45925 1.62758 3.36008 1.60425 3.26091 1.60425ZM8.13758 8.75592L8.04425 9.15258L8.20175 8.74425C8.17258 8.73841 8.14925 8.74425 8.13758 8.75592Z" fill="currentColor" />
                        </svg>
                        Phone
                    </button>
                    {/* <button className="btn-style-new">
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M5.24993 10.3541C5.01076 10.3541 4.81243 10.1558 4.81243 9.91662V7.47245L4.39243 7.89245C4.22326 8.06162 3.94326 8.06162 3.77409 7.89245C3.60493 7.72329 3.60493 7.44329 3.77409 7.27412L4.94076 6.10745C5.06326 5.98495 5.25576 5.94412 5.41909 6.01412C5.58243 6.07829 5.68743 6.24162 5.68743 6.41662V9.91662C5.68743 10.1558 5.48909 10.3541 5.24993 10.3541Z" fill="currentColor" />
                            <path d="M6.41667 8.02068C6.30584 8.02068 6.19501 7.97984 6.10751 7.89234L4.94084 6.72568C4.77167 6.55651 4.77167 6.27651 4.94084 6.10734C5.11001 5.93818 5.39001 5.93818 5.55917 6.10734L6.72584 7.27401C6.89501 7.44318 6.89501 7.72318 6.72584 7.89234C6.63834 7.97984 6.52751 8.02068 6.41667 8.02068Z" fill="currentColor" />
                            <path d="M8.75008 13.2709H5.25008C2.08258 13.2709 0.729248 11.9176 0.729248 8.75008V5.25008C0.729248 2.08258 2.08258 0.729248 5.25008 0.729248H8.16675C8.40592 0.729248 8.60425 0.927581 8.60425 1.16675C8.60425 1.40591 8.40592 1.60425 8.16675 1.60425H5.25008C2.56091 1.60425 1.60425 2.56091 1.60425 5.25008V8.75008C1.60425 11.4392 2.56091 12.3959 5.25008 12.3959H8.75008C11.4392 12.3959 12.3959 11.4392 12.3959 8.75008V5.83342C12.3959 5.59425 12.5942 5.39592 12.8334 5.39592C13.0726 5.39592 13.2709 5.59425 13.2709 5.83342V8.75008C13.2709 11.9176 11.9176 13.2709 8.75008 13.2709Z" fill="currentColor" />
                            <path d="M12.8334 6.27079H10.5001C8.50508 6.27079 7.72925 5.49495 7.72925 3.49995V1.16662C7.72925 0.991619 7.83425 0.828286 7.99758 0.764119C8.16091 0.694119 8.34758 0.734953 8.47591 0.857453L13.1426 5.52412C13.2651 5.64662 13.3059 5.83912 13.2359 6.00245C13.1659 6.16579 13.0084 6.27079 12.8334 6.27079ZM8.60425 2.22245V3.49995C8.60425 5.00495 8.99508 5.39579 10.5001 5.39579H11.7776L8.60425 2.22245Z" fill="currentColor" />
                        </svg>
                        Publish
                    </button> */}
                </div>
            </div>

            <div className='bottom-content-area'>
                {activeAgentTab === 'prompt' ? (
                    <>
                        {/* <h4>System Prompt</h4> */}
                        <div className={`agent-detail-bottom ${ishistoryTab ? 'histroy-tab-show' : ''}`}>
                            <Functions
                                agentId={id}
                                agentData={agentData}
                                setAgentData={setAgentData}
                                handleUpdateAgent={handleUpdateAgent}
                                setSelectedFile={setSelectedFile}
                            />
                            <div className='agent-detail-text'>

                        {/* ========== System Prompt Section ========== */}
                        <div className='agent-detail-box'>
                            <TopSettings agentData={agentData} setAgentData={setAgentData} setUpdated={setUpdated} rightSlot={<PromptTools agentId={id} onApplyAiPrompt={handleApplyAiPrompt} />} />
                            <div
                                contentEditable
                                suppressContentEditableWarning
                                onBlur={(e) => {
                                    const updatedScript = e['target']['innerHTML'].trim() || ' ';
                                    setAgentData((prev) => {
                                        if (prev['script'] !== updatedScript) setIsEdited(true);
                                        return { ...prev, script: updatedScript };
                                    })
                                }}
                                onInput={() => setIsEdited(true)}
                                dangerouslySetInnerHTML={{ __html: agentData?.['script'] || '' }}
                            />
                        </div>
                        {isEdited && (
                            <div className='agent-save-bar'>
                                <button className='btn-style-new' onClick={() => { handleUpdateAgent(); setIsEdited(false); }}>Save</button>
                            </div>
                        )}

                        {/* ========== Welcome Message Section ========== */}
                        <div className='welcome-msg'>
                            <h4>Welcome Message</h4>
                            <div className='group-form'>
                                <FormControl fullWidth>
                                    <Select
                                        displayEmpty
                                        open={openWelcome}
                                        value={agentData?.['welcomeMessageType'] ? constants['welcomeMessage'].find(opt => opt.value === agentData['welcomeMessageType'])?.label : 'AI Initiates: AI begins with your defined begin message.'}
                                        onOpen={() => setOpenWelcome(true)}
                                        onClose={() => setOpenWelcome(false)}
                                        onChange={(e) => handleWelcomeMessage(e['target']['value'])}
                                        renderValue={(selected) => {
                                            if (!selected || selected === 'AI Initiates: AI begins with your defined begin message.') {
                                                return 'AI Initiates: AI begins with your defined begin message.';
                                            }
                                            const selectedOption = constants['welcomeMessage'].find((option) => option['label'] === selected);
                                            return selectedOption ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <i style={{ marginRight: '10px' }}>{selectedOption.icon}</i>
                                                    <Typography sx={{ fontSize: '14px' }}>
                                                        <p>{selectedOption.label} <span>{selectedOption.detail}</span></p>
                                                    </Typography>
                                                </Box>
                                            ) : null;
                                        }}
                                        MenuProps={{ disableAutoFocusItem: true }}
                                    >
                                        {/* Show Filtered Options */}
                                        {filteredWelcomeMessage.length > 0 ? (
                                            filteredWelcomeMessage.map((status) => (
                                                <MenuItem key={status['label']} value={status['label']} onClick={() => handleWelcomeMessage(status['label'])}>
                                                    <i style={{ marginRight: '10px' }}>{status['icon']}</i> <p>{status['label']} <span>{status['detail']}</span></p>
                                                </MenuItem>
                                            ))
                                        ) : (<MenuItem disabled>No results found</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </div>

                            {agentData?.['welcomeMessageType'] !== 'dynamic' && agentData?.['welcomeMessageType'] !== 'silent' && (
                                <div className='group-form mb-0'>
                                    <TextField
                                        fullWidth
                                        hiddenLabel
                                        size='small'
                                        placeholder=''
                                        variant='standard'
                                        name='firstMessage'
                                        onBlur={() => handleUpdateAgent()}
                                        value={agentData?.['firstMessage'] || ''}
                                        onChange={(e) => setAgentData({ ...agentData, firstMessage: e['target']['value'] })}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ========== Test Agent Section ========== */}
                    <div className={`talk-speach-area ${expandedAgent ? 'expanded' : ''}`}>
                        <div className='veriables-show-box'>
                            <button className='btn-veriables style-two' onClick={() => setExpandedAgent((prev) => !prev)}>
                                <svg width="12" height="11" viewBox="0 0 12 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M5.66992 5.66699C5.97693 5.35994 5.97696 4.85 5.66992 4.54297L1.45703 0.330078C1.14999 0.0231352 0.640025 0.0230962 0.333008 0.330078C0.173734 0.489351 0.0996094 0.690917 0.0996094 0.892578C0.0996991 1.09408 0.173892 1.29494 0.333008 1.4541L3.91406 5.03418L3.98438 5.10547L3.91406 5.17578L0.333008 8.75586C0.0260034 9.0629 0.0260012 9.57285 0.333008 9.87988C0.640042 10.1869 1.14998 10.1869 1.45703 9.87988L5.66992 5.66699Z" fill="#4B5563" stroke="#FCFDFF" stroke-width="0.2" />
                                    <path d="M11.6699 5.66699C11.9769 5.35994 11.977 4.85 11.6699 4.54297L7.45703 0.330078C7.14999 0.0231352 6.64003 0.0230962 6.33301 0.330078C6.17373 0.489351 6.09961 0.690917 6.09961 0.892578C6.0997 1.09408 6.17389 1.29494 6.33301 1.4541L9.91406 5.03418L9.98438 5.10547L9.91406 5.17578L6.33301 8.75586C6.026 9.0629 6.026 9.57285 6.33301 9.87988C6.64004 10.1869 7.14998 10.1869 7.45703 9.87988L11.6699 5.66699Z" fill="#4B5563" stroke="#FCFDFF" stroke-width="0.2" />
                                </svg>
                            </button>
                            <button className='btn-veriables' onClick={toggleContentVeriablesBox}>&#123; &#125;</button>

                            {showVariablesBox && (
                                <div className="veriables-area">
                                    <h4>Dyanmic Variables</h4>
                                    <p>Set dyanmic variables for dashboard audio</p>
                                    <div className='veriables-list'>
                                        <div className='veriables-item'>
                                            <div className='header-values'>
                                                <span className='variable-name'>Variables Name</span>
                                                <span className='variable-name'>Test Value</span>
                                            </div>
                                            {variables.map((item, index) => (
                                                <div className='values-inputs' key={index}>
                                                    <div className='input-box'>
                                                        <TextField
                                                            fullWidth
                                                            hiddenLabel
                                                            size='small'
                                                            placeholder='Enter the variable name'
                                                            variant='standard'
                                                            name='name'
                                                            value={item.name}
                                                            onChange={(e) => handleVariablesAction('change', index, 'name', e.target.value)}
                                                        />
                                                    </div>
                                                    <div className='input-box'>
                                                        <TextField
                                                            fullWidth
                                                            hiddenLabel
                                                            size='small'
                                                            placeholder='Enter the value'
                                                            variant='standard'
                                                            name='value'
                                                            value={item.value}
                                                            onChange={(e) => handleVariablesAction('change', index, 'value', e.target.value)}
                                                        />
                                                    </div>
                                                    <button className='veriabel-removed' onClick={() => handleVariablesAction('remove', index)}>
                                                        <i className='icon fa fa-trash'></i>
                                                    </button>
                                                </div>
                                            ))}

                                            <button className='veriables-add-btn' onClick={() => handleVariablesAction('add')}>+ Add</button>
                                            <div className='veriables-footer'>
                                                <button className='btn-style-border' onClick={() => handleVariablesAction('cancel')}> Cancel </button>
                                                <button className='btn-style-new' onClick={() => handleVariablesAction('save')}> Save </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className='tabs-box'>
                            {/* <div className='top-app-bar'> */}
                            {/* <AppBar position='static'> */}
                            {/* <Tabs value={valueLeftTab} onChange={handleTabsSetLeft} aria-label='simple tabs example'> */}
                            {/* <Tab label='Test Audio' /> */}
                            {/* <Tab label='Test LLM' /> */}
                            {/* </Tabs> */}
                            {/* </AppBar> */}
                            {/* </div> */}
                            <div className='tabs-bottom'>
                                <div className='talk-area'>
                                    {!isAgentConnected ? (
                                        <div className='talk-box'>
                                            <i className='icon'>
                                                <svg width='56' height='56' viewBox='0 0 56 56' fill='none' xmlns='http://www.w3.org/2000/svg'>
                                                    <path d='M28 2.33301C26.1435 2.33301 24.363 3.07051 23.0503 4.38326C21.7375 5.69602 21 7.47649 21 9.33301V27.9997C21 29.8562 21.7375 31.6367 23.0503 32.9494C24.363 34.2622 26.1435 34.9997 28 34.9997C29.8565 34.9997 31.637 34.2622 32.9497 32.9494C34.2625 31.6367 35 29.8562 35 27.9997V9.33301C35 7.47649 34.2625 5.69602 32.9497 4.38326C31.637 3.07051 29.8565 2.33301 28 2.33301Z' stroke='#F2F2F2' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                                                    <path d='M44.3332 23.333V27.9997C44.3332 32.3315 42.6123 36.486 39.5492 39.5491C36.4862 42.6122 32.3317 44.333 27.9998 44.333C23.668 44.333 19.5135 42.6122 16.4504 39.5491C13.3873 36.486 11.6665 32.3315 11.6665 27.9997V23.333' stroke='#F2F2F2' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                                                    <path d='M28 44.333V53.6663' stroke='#F2F2F2' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                                                    <path d='M18.6665 53.667H37.3332' stroke='#F2F2F2' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                                                </svg>
                                            </i>
                                            <p>{agentData?.status === 'inactive' ? 'Agent is deactivated' : isLoading ? 'Loading agent...' : 'Test your agent'}</p>
                                            {renderMicSettings(
                                                <button
                                                    className='btn-style-border'
                                                    onClick={() => {
                                                        if (!agentData?._id) {
                                                            EventBus.publish('error', 'Please wait for agent data to load.');
                                                            return;
                                                        }
                                                        if (agentData?.status === 'inactive') {
                                                            EventBus.publish('error', 'This agent has been deactivated. Please contact your support team to reactivate it.');
                                                            return;
                                                        }
                                                        startAgent();
                                                    }}
                                                    disabled={isLoading || !agentData?._id || agentData?.status === 'inactive' || ecsWarmingUp}
                                                    style={(isLoading || !agentData?._id || agentData?.status === 'inactive' || ecsWarmingUp) ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                                                >
                                                    {isLoading ? 'Loading...' : agentData?.status === 'inactive' ? 'Deactivated' : 'Test'}
                                                </button>
                                            )}
                                            {ecsWarmingUp && (
                                                <div className='ecs-wakeup-dots'>
                                                    <div className='ecs-dots-row'>
                                                        <span className='ecs-dot'></span>
                                                        <span className='ecs-dot'></span>
                                                        <span className='ecs-dot'></span>
                                                        <span className='ecs-dot ecs-dot-center'></span>
                                                        <span className='ecs-dot'></span>
                                                        <span className='ecs-dot'></span>
                                                        <span className='ecs-dot'></span>
                                                    </div>
                                                    <p className='ecs-wakeup-text'>Agent is waking up, please wait 5–6 minutes...</p>
                                                </div>
                                            )}
                                        </div>

                                    ) : (
                                        <div className='talk-box style-two'>
                                            <i className='icon'>
                                                <svg width='56' height='56' viewBox='0 0 56 56' fill='none' xmlns='http://www.w3.org/2000/svg'>
                                                    <path d='M28 2.33301C26.1435 2.33301 24.363 3.07051 23.0503 4.38326C21.7375 5.69602 21 7.47649 21 9.33301V27.9997C21 29.8562 21.7375 31.6367 23.0503 32.9494C24.363 34.2622 26.1435 34.9997 28 34.9997C29.8565 34.9997 31.637 34.2622 32.9497 32.9494C34.2625 31.6367 35 29.8562 35 27.9997V9.33301C35 7.47649 34.2625 5.69602 32.9497 4.38326C31.637 3.07051 29.8565 2.33301 28 2.33301Z' stroke='#F2F2F2' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                                                    <path d='M44.3332 23.333V27.9997C44.3332 32.3315 42.6123 36.486 39.5492 39.5491C36.4862 42.6122 32.3317 44.333 27.9998 44.333C23.668 44.333 19.5135 42.6122 16.4504 39.5491C13.3873 36.486 11.6665 32.3315 11.6665 27.9997V23.333' stroke='#F2F2F2' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                                                    <path d='M28 44.333V53.6663' stroke='#F2F2F2' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                                                    <path d='M18.6665 53.667H37.3332' stroke='#F2F2F2' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                                                </svg>
                                            </i>
                                            <div className={`state-${speakingState}`}>
                                                <div className="state-dot"></div>
                                                <span className="state-text">
                                                    {speakingState.charAt(0).toUpperCase() + speakingState.slice(1)}
                                                </span>
                                                <div className="state-wave"></div>
                                            </div>
                                            {isRunning && <div className='timer-agent'> <p><span>{formattedMinutes}</span> : <span>{formattedSeconds}</span></p> </div>}
                                            {renderMicSettings(
                                                <button className='btn-style-red' onClick={() => { disconnectAgent() }}>
                                                    <i className='icon-end-call'>
                                                    <svg width='800px' height='800px' viewBox='0 0 24 24' version='1.1' xmlns='http://www.w3.org/2000/svg'>
                                                        <g id='🔍-Product-Icons' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'>
                                                            <g id='ic_fluent_call_inbound_24_filled' fill='#212121' fill-rule='nonzero'>
                                                                <path d='M9.36703096,3.31256396 L10.2267719,5.33990935 C10.6014206,6.22336423 10.3935244,7.26227205 9.71279082,7.9083973 L7.81847309,9.70640851 C7.93535507,10.7818874 8.29686524,11.8408843 8.90300361,12.8833992 C9.50914197,13.9259142 10.266164,14.7907132 11.1740696,15.4777964 L13.4492636,14.7191114 C14.3116668,14.4315348 15.2508597,14.7620211 15.7798528,15.5392067 L17.0122122,17.349763 C17.6271206,18.2531736 17.516541,19.4995134 16.7535143,20.2655387 L15.9357982,21.0864601 C15.1218751,21.9035819 13.9593315,22.1999669 12.8839268,21.8645204 C10.3450716,21.0725857 8.0107575,18.7213775 5.88098445,14.8108958 C3.74811387,10.8947267 2.9953647,7.57213801 3.62273695,4.84312972 C3.88673284,3.6948166 4.70424816,2.78033469 5.77175058,2.43923166 L6.84834294,2.09522409 C7.85716063,1.77287281 8.93501107,2.29382273 9.36703096,3.31256396 Z M14.4978449,8.43869174 L20.7184544,2.21549812 C21.0112723,1.92252955 21.486146,1.92240737 21.7791146,2.21522523 C22.0454496,2.48142327 22.0697629,2.89808071 21.851984,3.19174825 L21.7793874,3.27588536 L15.5588449,9.49869174 L20.25,9.49870377 C20.6296958,9.49870377 20.943491,9.78085765 20.9931534,10.1469332 L21,10.2487038 C21,10.6283995 20.7178461,10.9421947 20.3517706,10.9918572 L20.25,10.9987038 L13.695179,10.9979536 L13.695179,10.9979536 L13.5970114,10.984476 L13.5970114,10.984476 L13.5016309,10.9582429 L13.5016309,10.9582429 L13.4337826,10.9296888 L13.4337826,10.9296888 C13.3555282,10.8948112 13.2825295,10.8446834 13.2182796,10.7804665 L13.1775595,10.7364313 L13.1775595,10.7364313 L13.1525474,10.7041493 L13.1525474,10.7041493 L13.1050738,10.6355511 L13.1050738,10.6355511 L13.0588237,10.544137 L13.0588237,10.544137 L13.0264601,10.4525511 L13.0264601,10.4525511 L13.0102057,10.3825136 L13.0102057,10.3825136 L13.0002327,10.3015013 L13.0002327,10.3015013 L12.9984732,10.2487038 L12.9984732,3.74481332 C12.9984732,3.33059976 13.3342596,2.99481332 13.7484732,2.99481332 C14.128169,2.99481332 14.4419642,3.2769672 14.4916266,3.64304276 L14.4984732,3.74481332 L14.4978449,8.43869174 L20.7184544,2.21549812 L14.4978449,8.43869174 Z' id='🎨-Color'>
                                                                </path>
                                                            </g>
                                                        </g>
                                                    </svg>
                                                </i>
                                                End Call
                                            </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ========== Histroy Section ========== */}
                    {ishistoryTab && (
                        <div className='talk-speach-area history-area-tab'>
                            <div className='tabs-box'>
                                <div className='tabs-bottom'>
                                    <div className='history-box-area'>
                                        <div className="history-title-area">
                                            <h4>History</h4>
                                            <button className='btn-clear-history' onClick={() => setIsHistoryTab(false)}><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="currentColor"><path d="M9.99956 8.93949L13.7121 5.22699L14.7726 6.28749L11.0601 9.99999L14.7726 13.7125L13.7121 14.773L9.99956 11.0605L6.28706 14.773L5.22656 13.7125L8.93906 9.99999L5.22656 6.28749L6.28706 5.22699L9.99956 8.93949Z" fill="currentColor"></path></svg></button>
                                        </div>
                                        <div className='history-search'>
                                            <div className='searchbox'>
                                                <TextField
                                                    fullWidth
                                                    hiddenLabel
                                                    size="small"
                                                    name='search'
                                                    variant="standard"
                                                    placeholder='Search...'
                                                // value={searchText}
                                                // onChange={(e) => setSearchText(e['target']['value'])}
                                                />
                                                <i className='icon'>
                                                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M5.82319 10.6464C3.16455 10.6464 1 8.48183 1 5.82319C1 3.16455 3.16455 1 5.82319 1C8.48183 1 10.6464 3.16455 10.6464 5.82319C10.6464 8.48183 8.48183 10.6464 5.82319 10.6464ZM5.82319 1.70583C3.55041 1.70583 1.70583 3.55511 1.70583 5.82319C1.70583 8.09127 3.55041 9.94055 5.82319 9.94055C8.09597 9.94055 9.94055 8.09127 9.94055 5.82319C9.94055 3.55511 8.09597 1.70583 5.82319 1.70583Z" fill="#505258" stroke="#505258" stroke-width="0.236842" />
                                                        <path d="M13.3182 13.7068C13.2245 13.7069 13.1307 13.6725 13.0566 13.5985L9.10425 9.63167C8.96097 9.48877 8.96065 9.25192 9.10355 9.10864C9.24646 8.96536 9.4833 8.96505 9.62658 9.10795L13.5789 13.0748C13.7222 13.2177 13.7225 13.4546 13.5796 13.5979C13.5057 13.672 13.412 13.7066 13.3182 13.7068Z" fill="#505258" stroke="#505258" stroke-width="0.236842" />
                                                    </svg>
                                                </i>
                                            </div>
                                            <div className='history-list'>
                                                <div className='history-item'>
                                                    <div className='history-user'>
                                                        <i className="icon"></i>
                                                        <p>Draft - <span>V0</span></p>
                                                    </div>
                                                </div>
                                                <div className='history-item'>
                                                    <div className='history-user'>
                                                        <i className="icon"></i>
                                                        <p>Draft - <span>V0</span></p>
                                                    </div>
                                                </div>
                                                <div className='history-item'>
                                                    <div className='history-user'>
                                                        <i className="icon"></i>
                                                        <p>Draft - <span>V0</span></p>
                                                    </div>
                                                </div>
                                                <div className='history-item'>
                                                    <div className='history-user'>
                                                        <i className="icon"></i>
                                                        <p>Draft - <span>V0</span></p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                    </>
                ) : (
                    <CallLogs agentId={id} />
                )}
            </div>

            <style jsx>{`
                .agent-state-container {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 1rem;
                    padding: 1rem;
                }

                .state-indicator {
                    position: relative;
                    width: 100%;
                    max-width: 150px;
                    padding: 0.5rem 1rem;
                    border-radius: 22px;
                    background: linear-gradient(120deg, rgba(255,255,255,0.35) 60%, rgba(200,220,255,0.25) 100%);
                    box-shadow: 0 4px 16px 0 rgba(31, 38, 135, 0.13);
                    backdrop-filter: blur(10px);
                    border: 1px solid rgba(255,255,255,0.18);
                    transition: all 0.3s cubic-bezier(.4,2,.6,1);
                    overflow: hidden;
                    z-index: 2;
                }

                .state-content {
                    display: flex;
                    align-items: center;
                    gap: 0.6rem;
                    position: relative;
                    z-index: 2;
                }

                .state-dot {
                    width: 10px;
                    height: 10px;
                    border-radius: 50%;
                    animation: pulse 2s infinite;
                }

                .state-text {
                    font-size: 0.95rem;
                    font-weight: 600;
                    margin: 0;
                    letter-spacing: 0.3px;
                    transition: all 0.3s ease;
                    text-shadow: 0 2px 8px rgba(0,0,0,0.08);
                }

                .state-wave {
                    position: absolute;
                    bottom: 0;
                    left: 0;
                    width: 100%;
                    height: 4px;
                    opacity: 0.85;
                    filter: blur(0.5px);
                }

                /* Speaking State */
                .state-speaking {
                    background: linear-gradient(120deg, rgba(33,150,243,0.18) 60%, rgba(100,181,246,0.13) 100%);
                }
                .state-speaking .state-text {
                    color: #2196F3;
                }
                .state-speaking .state-dot {
                    background: radial-gradient(circle at 40% 40%, #2196F3 70%, #64B5F6 100%);
                    box-shadow: 0 0 10px #2196F3, 0 0 16px #64B5F6;
                }
                .state-speaking .state-wave {
                    background: repeating-linear-gradient(90deg, #2196F3 0 10px, #64B5F6 10px 20px);
                    animation: waveSpeaking 1.5s infinite linear;
                }

                /* Listening State */
                .state-listening {
                    background: linear-gradient(120deg, rgba(76,175,80,0.18) 60%, rgba(129,199,132,0.13) 100%);
                }
                .state-listening .state-text {
                    color: #4CAF50;
                }
                .state-listening .state-dot {
                    background: radial-gradient(circle at 40% 40%, #4CAF50 70%, #81C784 100%);
                    box-shadow: 0 0 10px #4CAF50, 0 0 16px #81C784;
                }
                .state-listening .state-wave {
                    background: repeating-linear-gradient(90deg, #4CAF50 0 10px, #81C784 10px 20px);
                    animation: waveListening 1.5s infinite linear;
                }

                /* Idle State */
                .state-idle {
                    background: linear-gradient(120deg, rgba(158,158,158,0.13) 60%, rgba(189,189,189,0.10) 100%);
                }
                .state-idle .state-text {
                    color: #9E9E9E;
                }
                .state-idle .state-dot {
                    background: radial-gradient(circle at 40% 40%, #9E9E9E 70%, #BDBDBD 100%);
                    box-shadow: 0 0 7px #9E9E9E, 0 0 10px #BDBDBD;
                }
                .state-idle .state-wave {
                    background: repeating-linear-gradient(90deg, #9E9E9E 0 10px, #BDBDBD 10px 20px);
                    animation: waveIdle 2.2s infinite linear;
                }

                @keyframes pulse {
                    0% { transform: scale(1); opacity: 1; }
                    50% { transform: scale(1.2); opacity: 0.7; }
                    100% { transform: scale(1); opacity: 1; }
                }

                @keyframes waveSpeaking {
                    0% { background-position-x: 0; }
                    100% { background-position-x: 150px; }
                }

                @keyframes waveListening {
                    0% { background-position-x: 0; }
                    100% { background-position-x: 150px; }
                }

                @keyframes waveIdle {
                    0% { background-position-x: 0; }
                    100% { background-position-x: 150px; }
                }

                .state-indicator:hover {
                    transform: translateY(-2px) scale(1.03);
                    box-shadow: 0 4px 16px 0 rgba(31, 38, 135, 0.18);
                }

                .agent-state-indicator {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    margin-top: 12px;
                    padding: 8px 16px;
                    border-radius: 20px;
                    background: rgba(255, 255, 255, 0.1);
                    backdrop-filter: blur(4px);
                }

                .state-dot {
                    width: 8px;
                    height: 8px;
                    border-radius: 50%;
                    animation: pulse 2s infinite;
                }

                .state-text {
                    font-size: 14px;
                    font-weight: 500;
                }

                /* Speaking state */
                .state-dot.speaking {
                    background: #2196F3;
                    box-shadow: 0 0 8px #2196F3;
                }
                .state-text.speaking {
                    color: #2196F3;
                }

                /* Listening state */
                .state-dot.listening {
                    background: #4CAF50;
                    box-shadow: 0 0 8px #4CAF50;
                }
                .state-text.listening {
                    color: #4CAF50;
                }

                /* Idle state */
                .state-dot.idle {
                    background: #9E9E9E;
                    box-shadow: 0 0 8px #9E9E9E;
                }
                .state-text.idle {
                    color: #9E9E9E;
                }

                @keyframes pulse {
                    0% { transform: scale(1); opacity: 1; }
                    50% { transform: scale(1.2); opacity: 0.7; }
                    100% { transform: scale(1); opacity: 1; }
                }
            `}</style>
            <PhoneModal
                isPhoneModal={isPhoneModal}
                togglePhoneModal={togglePhoneModal}
                phones={allPhones}
                agentId={id}
                agentData={agentData}
                onPhoneAssign={(phoneData) => {
                    // Update agent with selected phone
                    setAgentData(prev => ({
                        ...prev,
                        phone: phoneData.phoneNumber,
                        inboundAgentId: phoneData.inboundAgentId,
                        outboundAgentId: phoneData.outboundAgentId
                    }));
                    setUpdated(true);
                }}
            />
        </div>
    );
}


export default SingleAgent